/**
 * SyncStatusIndicator - Indicador de estado de sincronización de red
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/components
 *
 * @description
 * Muestra el estado de sincronización de operaciones de red:
 * - Sincronizando: Hay transacciones pendientes
 * - Sincronizado: No hay transacciones pendientes
 *
 * @example
 * ```tsx
 * <SyncStatusIndicator />
 * ```
 */

import { useState, useEffect } from 'react';
import { networkTransactionService } from '../services/network-transaction.service';
import './sync-status-indicator.css';

export interface SyncStatusIndicatorProps {
  /** Si se muestra en el modo compacto (por defecto: false) */
  compact?: boolean;
}

/**
 * Indicador de estado de sincronización
 */
export function SyncStatusIndicator({ compact = false }: SyncStatusIndicatorProps): JSX.Element | null {
  const [hasPendingTransactions, setHasPendingTransactions] = useState(false);

  useEffect(() => {
    // Polling cada 500ms para verificar transacciones pendientes
    const interval = setInterval(() => {
      const pending = networkTransactionService.hasPendingTransactions();
      setHasPendingTransactions(pending);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // No mostrar nada si no hay transacciones pendientes
  if (!hasPendingTransactions) {
    return null;
  }

  return (
    <div className={`sync-status-indicator ${compact ? 'sync-status-indicator--compact' : ''}`}>
      <div className="sync-status-indicator__spinner" />
      {!compact && <span className="sync-status-indicator__text">Sincronizando...</span>}
    </div>
  );
}
